import { ApiAccess } from '../enums/ApiAccess';
import { getUTCDateByDate } from '../helpers/TimeHelper';
import { ConsumerFormViewModel } from '../models/Consumer/ConsumerFormViewModel';
import { ScopesPresetModel } from '../models/Consumer/Scopes/ScopesPresetModel';
import { SmIdsModel } from '../models/Consumer/SmIdsModel';
import { TripsModel } from '../models/Consumer/ApiAccess/TripsModel';
import { ConsumerResponseModel } from './../models/Consumer/ConsumerResponseModel';
import { ConsumerRequestModel } from '../models/Consumer/ConsumerRequestModel';
import { WaiversModel } from '../models/Consumer/ApiAccess/WaiversModel';
import { TripsApiAllowedSchemas } from '../constants/tripsApiAllowedSchemas';

export const mapToConsumerModel = (
  model: ConsumerFormViewModel,
  scopesPresets: ScopesPresetModel[],
  prevModel?: ConsumerResponseModel,
): ConsumerRequestModel => {
  const result = {
    ...prevModel,
    consumerId: model.consumerId,
    consumerKey: model.consumerKey ?? '',
    hasOauthAccess: model.hasOauthAccess,
    enabled: model.enabled,
    activeStartDate: model.activeStartDate && getUTCDateByDate(model.activeStartDate).toJSON(),
    activeEndDate: model.activeEndDate && getUTCDateByDate(model.activeEndDate).toJSON(),
    contacts: model.contacts.map((c) => c.model),
    consumerTypes: model.consumerTypes,
    trips: mapTripsModel(model, scopesPresets, prevModel),
    expenses: mapApiAuthorizationProperty(model, ApiAccess.Expenses),
    waivers: mapWaiversProperty(model),
    xx1: mapApiAuthorizationProperty(model, ApiAccess.Xx1),
    receipts: mapApiAuthorizationProperty(model, ApiAccess.Receipts),
    tickets: mapApiAuthorizationProperty(model, ApiAccess.Tickets),
    flightAlerts: mapApiAuthorizationProperty(model, ApiAccess.FlightAlerts),
    enhApi: mapApiAuthorizationProperty(model, ApiAccess.Enh),
  };

  return mapObjectPropertiesFromPrevModel(result, prevModel);
};

const mapTripsModel = (
  model: ConsumerFormViewModel,
  scopesPresets: ScopesPresetModel[],
  prevModel?: ConsumerResponseModel,
): TripsModel | undefined => {
  const isTripEnabled = hasApiAccess(model, ApiAccess.Trips);

  const scopesFromDefaultPreset = scopesPresets.find((s) => s.isDefault)?.preset;

  const allowedSchemas = mapAllowedSchemas(model, prevModel);

  const isNewConsumerWithoutTripAccess = !model.consumerId && !isTripEnabled;
  if (isNewConsumerWithoutTripAccess) {
    return { scopes: scopesFromDefaultPreset, allowedSchemas: allowedSchemas };
  }

  if (isTripEnabled) {
    const areTripsDisabledOnPrevModel = !prevModel?.trips?.enabled && !prevModel?.trips?.smIds;
    if (!model.consumerId || (model.consumerId && areTripsDisabledOnPrevModel)) {
      return {
        enabled: true,
        smIds: { isInclusive: true } as SmIdsModel,
        scopes: scopesFromDefaultPreset,
        tripChanges: { enabled: false },
        allowedSchemas: allowedSchemas,
      };
    }
  }

  return { enabled: isTripEnabled, allowedSchemas: allowedSchemas };
};

const mapAllowedSchemas = (model: ConsumerFormViewModel, prevModel?: ConsumerResponseModel): string[] => {
  const availableAllowedSchemas: { access: ApiAccess; format: string }[] = [
    { access: ApiAccess.TripsDefault, format: TripsApiAllowedSchemas.default },
    { access: ApiAccess.TripsInvoices, format: TripsApiAllowedSchemas.invoices },
    { access: ApiAccess.TripsItinerary, format: TripsApiAllowedSchemas.itinerary },
    { access: ApiAccess.TripsMetadata, format: TripsApiAllowedSchemas.metadata },
  ];

  const result = [...(prevModel?.trips?.allowedSchemas?.map((x) => x.toLowerCase()) || [])];
  for (const { access, format } of availableAllowedSchemas) {
    const formatExistingindex = result.indexOf(format);
    const formatEnabled = hasApiAccess(model, access);
    if (formatExistingindex >= 0 && !formatEnabled) {
      //if format was initally set, but then disabled
      // delete format from the list of existing formats for consumer
      result.splice(formatExistingindex, 1);
    } else if (formatExistingindex < 0 && formatEnabled) {
      // if format was NOT initially set, but then enabled
      // add format to the list of existing formats for consumer
      result.push(format);
    }
  }
  return result;
};

const mapApiAuthorizationProperty = (
  model: ConsumerFormViewModel,
  apiAccess: ApiAccess,
): { enabled: boolean } | undefined => {
  const isEnabled = hasApiAccess(model, apiAccess);

  if (!isEnabled && !model.consumerId) {
    return undefined;
  }

  return { enabled: isEnabled };
};

const mapWaiversProperty = (model: ConsumerFormViewModel): WaiversModel | undefined => {
  return hasApiAccess(model, ApiAccess.WaiversStandAlone)
    ? { enabled: true, match: hasApiAccess(model, ApiAccess.WaiversMatching) }
    : undefined;
};

const mapObjectPropertiesFromPrevModel = (
  model: ConsumerRequestModel,
  prevModel?: ConsumerResponseModel,
): ConsumerRequestModel => {
  if (!prevModel) {
    return model;
  }

  const result: ConsumerRequestModel = { ...model };

  for (const [k, v] of Object.entries(result) as [keyof ConsumerResponseModel, ConsumerResponseModel][]) {
    if (typeof v === 'object' && !Array.isArray(v) && k in prevModel) {
      result[k] = { ...(prevModel[k] as ConsumerResponseModel), ...v } as never;
    }
  }

  return result;
};

const hasApiAccess = (model: ConsumerFormViewModel, apiAccess: ApiAccess): boolean =>
  model.apiAccess?.includes(apiAccess) ?? false;
